import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LeadImportBatch,
  LeadImportRecord,
  leadImportService,
} from '../services/leadImportService';
import { DataListTable, type Column } from '../components/crm/DataListTable';

const LeadImportDetail: React.FC = () => {
  const { batchId = '' } = useParams();
  const [batch, setBatch] = useState<LeadImportBatch | null>(null);
  const [records, setRecords] = useState<LeadImportRecord[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [editingRecordId, setEditingRecordId] = useState<string>('');
  const [editJson, setEditJson] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setError('');
    try {
      const [batchData, recordData] = await Promise.all([
        leadImportService.get(batchId),
        leadImportService.listRecords(batchId, statusFilter ? { status: statusFilter } : {}),
      ]);
      setBatch(batchData);
      setRecords(recordData.results || []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load import batch.');
    } finally {
      setLoading(false);
    }
  }, [batchId, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const pendingCount = useMemo(
    () => records.filter((record) => record.import_status === 'pending_review').length,
    [records]
  );

  const approveRecord = async (recordId: string) => {
    if (!batchId) return;
    setActionLoading(true);
    try {
      await leadImportService.approveRecord(batchId, recordId);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to approve record.');
    } finally {
      setActionLoading(false);
    }
  };

  const rejectRecord = async (recordId: string) => {
    if (!batchId) return;
    setActionLoading(true);
    try {
      await leadImportService.rejectRecord(batchId, recordId);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to reject record.');
    } finally {
      setActionLoading(false);
    }
  };

  const bulkApprove = async () => {
    if (!batchId) return;
    setActionLoading(true);
    try {
      await leadImportService.bulkApprove(batchId);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to bulk approve.');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditor = (record: LeadImportRecord) => {
    setEditingRecordId(record.id);
    setEditJson(JSON.stringify(record.normalized_data || {}, null, 2));
  };

  const saveEdit = async () => {
    if (!batchId || !editingRecordId) return;
    setActionLoading(true);
    try {
      const parsed = JSON.parse(editJson);
      await leadImportService.patchRecord(batchId, editingRecordId, parsed);
      setEditingRecordId('');
      setEditJson('');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Failed to update normalized data.');
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo<Column<LeadImportRecord>[]>(
    () => [
      {
        id: 'status',
        header: 'Status',
        mobileLabel: 'Status',
        render: (record) => <span className="text-sm text-fg-secondary">{record.import_status}</span>,
      },
      {
        id: 'company',
        header: 'Company / Contact',
        mobilePriority: 'primary',
        mobileLabel: 'Company / Contact',
        render: (record) => {
          const data = record.normalized_data || {};
          return (
            <>
              <span className="text-sm text-fg">{(data.company_name as string) || '-'}</span>
              <div className="text-xs text-fg-secondary">{(data.contact_name as string) || '-'}</div>
            </>
          );
        },
      },
      {
        id: 'contact',
        header: 'Email / Phone',
        mobileLabel: 'Email / Phone',
        render: (record) => {
          const data = record.normalized_data || {};
          return (
            <>
              <span className="text-sm text-fg-secondary">{(data.email as string) || '-'}</span>
              <div className="text-xs text-fg-secondary">{(data.phone as string) || '-'}</div>
            </>
          );
        },
      },
      {
        id: 'validation',
        header: 'Validation',
        mobileLabel: 'Validation',
        render: (record) => (
          <span className="text-sm text-fg-secondary">
            {record.validation_errors?.length ? record.validation_errors.join('; ') : 'OK'}
          </span>
        ),
      },
      {
        id: 'duplicate',
        header: 'Duplicate',
        mobileLabel: 'Duplicate',
        render: (record) => (
          <span className="text-sm text-fg-secondary">{record.duplicate_reason || '-'}</span>
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        mobileLabel: 'Actions',
        render: (record) => (
          <div data-row-action className="flex flex-wrap gap-2">
            <button
              className="btn-secondary"
              onClick={() => openEditor(record)}
              disabled={actionLoading}
            >
              Edit
            </button>
            <button
              className="btn-primary"
              onClick={() => approveRecord(record.id)}
              disabled={actionLoading || record.import_status === 'imported'}
            >
              Approve
            </button>
            <button
              className="btn-ghost"
              onClick={() => rejectRecord(record.id)}
              disabled={actionLoading || record.import_status === 'imported'}
            >
              Reject
            </button>
          </div>
        ),
      },
    ],
    [actionLoading]
  );

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <Link to="/lead-imports" className="text-sm text-primary hover:underline">
            Back to Lead Imports
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-fg">{batch?.name || 'Lead Import Batch'}</h1>
          <p className="mt-1 text-sm text-fg-secondary">Review imported records and approve valid leads.</p>
        </div>
        <button className="btn-primary" onClick={bulkApprove} disabled={actionLoading || pendingCount === 0}>
          Bulk Approve Valid
        </button>
      </div>

      {batch && (
        <div className="mb-4 grid gap-3 md:grid-cols-5">
          <SummaryCard label="Total" value={batch.total_records} />
          <SummaryCard label="Imported" value={batch.accepted_count} />
          <SummaryCard label="Rejected" value={batch.rejected_count} />
          <SummaryCard label="Duplicate" value={batch.duplicate_count} />
          <SummaryCard label="Failed" value={batch.failed_count} />
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        <select className="input-field max-w-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="pending_review">Pending review</option>
          <option value="duplicate">Duplicate</option>
          <option value="failed">Failed</option>
          <option value="imported">Imported</option>
          <option value="rejected">Rejected</option>
        </select>
        <button className="btn-secondary" onClick={load} disabled={loading}>
          Refresh
        </button>
      </div>

      {error && <div className="mb-4 rounded-md border border-danger/30 bg-danger/10 p-3 text-sm text-danger">{error}</div>}

      <DataListTable<LeadImportRecord>
        columns={columns}
        rows={records}
        getRowKey={(record) => record.id}
        loading={loading}
        emptyContent={
          <div className="rounded-lg border border-border bg-surface p-6 text-sm text-fg-secondary">
            No records in this batch.
          </div>
        }
      />

      {editingRecordId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--color-bg-overlay)] p-4">
          <div className="w-full max-w-3xl rounded-lg border border-border bg-surface p-5">
            <h3 className="mb-3 text-lg font-semibold text-fg">Edit Normalized Data</h3>
            <textarea
              className="input-field min-h-[320px] w-full font-mono text-sm"
              value={editJson}
              onChange={(e) => setEditJson(e.target.value)}
            />
            <div className="mt-4 flex justify-end gap-2">
              <button className="btn-ghost" onClick={() => setEditingRecordId('')}>
                Cancel
              </button>
              <button className="btn-primary" onClick={saveEdit} disabled={actionLoading}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-lg border border-border bg-surface p-4">
    <p className="text-xs uppercase tracking-wide text-fg-secondary">{label}</p>
    <p className="mt-1 text-2xl font-semibold text-fg">{value}</p>
  </div>
);

export default LeadImportDetail;
